/**
 * To Scale — dealing, scoring and wording.
 *
 * Kept out of the component so the maths can be tested without a canvas, and
 * so the one place that decides what a guess is worth is easy to find.
 */

import { MEASURES, PER_RUN } from '../data/measures.js';

/**
 * Deal a fresh run.
 *
 * Fisher–Yates rather than `sort(() => Math.random() - 0.5)`, which is not a
 * shuffle — it biases toward the original order, so the same items would keep
 * turning up in the same places and the pool would feel smaller than it is.
 */
export function dealRun(pool = MEASURES, count = PER_RUN) {
  const deck = pool.slice();
  for (let i = deck.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [deck[i], deck[j]] = [deck[j], deck[i]];
  }
  return deck.slice(0, count);
}

/**
 * What a guess is worth, 0–100.
 *
 * Scored on ratio, not on the difference. Being twenty feet out on the ark is
 * a near miss; being twenty feet out on the ark of the covenant means you had
 * no idea. A points system built on raw feet would say those were the same
 * answer, and would make the big items the only ones that mattered.
 *
 * Symmetric in the same way: guessing double and guessing half are both "out
 * by a factor of two", so they score the same. Straight proportional error is
 * not symmetric — it caps at 100% under and runs to infinity over — which
 * would quietly punish optimism and reward guessing small.
 */
export function scoreGuess(guess, truth) {
  if (!(guess > 0) || !(truth > 0)) return 0;
  const ratio = guess > truth ? guess / truth : truth / guess;
  // A factor of 3 out or worse scores nothing; dead on scores 100.
  return Math.max(0, Math.round(100 * (1 - Math.min(Math.log(ratio) / Math.log(3), 1))));
}

/** How wrong, said the way a person would say it. */
export function offBy(guess, truth) {
  if (!(guess > 0) || !(truth > 0)) return '';
  const ratio = guess > truth ? guess / truth : truth / guess;
  if (ratio < 1.04) return 'You had it';
  const factor = ratio < 10 ? ratio.toFixed(1) : Math.round(ratio);
  return guess > truth ? `${factor}× too big` : `${factor}× too small`;
}

/**
 * Feet and inches up to twenty feet, whole feet above that.
 *
 * The cut-off is not cosmetic: several answers land on a half foot — Og's bed
 * is 13ft 6in and the altar 7ft 6in — and rounding those to whole feet quietly
 * misreports the verse. Above twenty feet the inches stop carrying meaning.
 */
export function feet(ft) {
  if (!(ft > 0)) return '—';
  if (ft < 20) {
    const whole = Math.floor(ft);
    const inches = Math.round((ft - whole) * 12);
    if (inches === 12) return `${whole + 1} ft`;
    return inches ? `${whole} ft ${inches} in` : `${whole} ft`;
  }
  return `${Math.round(ft)} ft`;
}

/** The same figure in metres, for everyone who does not think in feet. */
export function metres(ft) {
  if (!(ft > 0)) return '—';
  const m = ft * 0.3048;
  return `${m < 6 ? m.toFixed(2) : Math.round(m)} m`;
}

/**
 * The slider runs on a log scale.
 *
 * The ark is 450ft and the ark of the covenant is under four. On a linear
 * slider the small answers all collapse into the first pixel, and the only way
 * to answer "about four feet" would be to nudge one step off the end stop.
 * Log spacing gives every order of magnitude the same amount of travel, which
 * is how people actually think about size.
 */
export function toSlider(value, min, max) {
  return (Math.log(value) - Math.log(min)) / (Math.log(max) - Math.log(min));
}

export function fromSlider(t, min, max) {
  return Math.exp(Math.log(min) + (Math.log(max) - Math.log(min)) * t);
}

/**
 * What to call a total out of five hundred.
 *
 * The bands are set from what the measurements actually do to people: nobody
 * gets the ark of the covenant, so a middling score is the honest common case
 * and should not read as failure.
 */
const RANKS = [
  { at: 460, name: 'You have read the measurements' },
  { at: 380, name: 'Surveyor' },
  { at: 290, name: 'Good eye' },
  { at: 190, name: 'Getting the feel of it' },
  { at: 0, name: 'Nobody pictures these right' },
];

export function rankFor(total) {
  return RANKS.find((rank) => total >= rank.at).name;
}

/** Something worth pasting somewhere. */
export function shareRun(total, max, rounds = []) {
  const bar = rounds
    .map((r) => (r.points >= 90 ? '🟩' : r.points >= 60 ? '🟨' : r.points >= 30 ? '🟧' : '⬛'))
    .join('');
  return [
    `To Scale — ${total}/${max}`,
    bar,
    rankFor(total),
    'scripturejourney.net',
  ]
    .filter(Boolean)
    .join('\n');
}

/** The height of an average adult man, used as the yardstick throughout. */
export const MAN_FT = 5.9;

/**
 * The same measurement said in people.
 *
 * At true scale a man beside the ark is four pixels tall, which is honest and
 * completely useless as a reference. Rather than quietly enlarge him — the
 * drawing would then be a lie, and the lie would be the one thing the game
 * exists to correct — the comparison is also given in words, which stays exact
 * at any size and is the form people actually repeat to each other.
 */
export function inPeople(ft, dim) {
  if (!(ft > 0)) return '';
  const n = ft / MAN_FT;
  const round = n < 10 ? n.toFixed(1) : Math.round(n);
  if (dim === 'height') {
    if (n < 1.08) return 'about the height of a man';
    return `${round} times the height of a man`;
  }
  if (n < 1.08) return 'shorter than a man is tall';
  return `${round} men lying end to end`;
}
